import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, MapPin, BookOpen, Clock } from "react-feather";
import { createNewOrder, fetchUserOrders } from "../../../features/orderSlice";
import { fetchUserAddress } from "../../../features/addressSlice";
import BookManager from "../../../services/home/BookManager";
import config from "../../../config";
import SkeletonLoader from "./SkeletonLoader";
import { fetchCurrentSubscription } from "../../../features/subscriptionSlice";
import AddAddressModal from "../addressComponents/AddAddressModal";
import AddressManager from "../../../services/dashboard/AddressManager";
import CityManager from "../../../services/dashboard/CityManager";

const OrderConfirmationPage = () => {
  // ... [keep existing state and hooks] ...
  const { bookId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAuthenticated } = useSelector((state) => state.auth);
  const userId = useSelector((state) => state.auth.user?.userId);
  const subscription = useSelector((state) => state.subscription.subscription);
  const { userOrders } = useSelector((state) => state.orders);

  const [allowedBookLimit, setAllowedBookLimit] = useState(0);
  const [remainingBooks, setRemainingBooks] = useState(0);

  const [loading, setLoading] = useState(true);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [order, setOrder] = useState(null);
  const [bookDetails, setBookDetails] = useState(null);
  const fullState = useSelector((state) => state);
  const [isModalOpen, setModalOpen] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);

  const addressState = useSelector((state) => state.address || {});

  const rawAddress = addressState.address;

  const transformedAddress = rawAddress
    ? {
        street: rawAddress.fullAddress || "",
        city: rawAddress.city?.cityName || "",
        state: rawAddress.city?.state || "",
        zipCode: rawAddress.pincode?.pinCode || "",
      }
    : null;

  useEffect(() => {
    async function loadMeta() {
      try {
        const cities = await CityManager.getAllCities();
        console.log("🟢 fetched cities:", cities);
        setCityOptions(cities.map((c) => ({ id: c.cityId, name: c.cityName })));
        const allPins = cities.flatMap((c) =>
          c.pincodes.map((p) => ({ id: p.pinId, code: p.pinCode }))
        );
        setPincodeOptions(allPins);
      } catch (err) {
        console.error("Failed to load cities/pincodes:", err);
      }
    }

    loadMeta();
  }, []); // ← empty deps means run once

  useEffect(() => {
    const init = async () => {
      if (!isAuthenticated) {
        toast.error("Please log in first.");
        navigate("/login");
        return;
      }

      try {
        const latestSubscription = await dispatch(
          fetchCurrentSubscription(userId)
        ).unwrap();
        const latestOrders = await dispatch(fetchUserOrders(userId)).unwrap();
        await dispatch(fetchUserAddress(userId)).unwrap();

        const book = await BookManager.getBookById(bookId);

        // Transforming book data
        const transformedBook = {
          title: book.bookName,
          images: book.bookImages?.map((img) => ({ url: img })) || [],
          author: {
            name: book.author?.authorName || "Unknown",
          },
          category: {
            name: book.category?.catName || "N/A",
          },
          description: book.bookDescription,
        };

        setBookDetails(transformedBook);

        const bookLimit =
          {
            ONE_BOOK: 1,
            TWO_BOOKS: 2,
            THREE_BOOKS: 3,
          }[latestSubscription?.subscription?.packageType] || 0;

        const activeOrdersCount = (latestOrders || []).filter(
          (o) => o?.returnStatus === "NOT_RETURNED"
        ).length;

        setAllowedBookLimit(bookLimit);
        setRemainingBooks(bookLimit - activeOrdersCount);
      } catch (err) {
        toast.error("Failed to fetch data.");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [dispatch, isAuthenticated, userId, navigate, bookId]);

  const handleOrder = async () => {
    if (!subscription) {
      toast.error("You need an active subscription to rent books.");
      navigate("/subscriptions");
      return;
    }

    if (subscription.status !== "ACTIVE") {
      toast.error("Your subscription is not active.");
      navigate("/subscriptions");
      return;
    }

    if (
      subscription.remainingDays !== undefined &&
      subscription.remainingDays <= 0
    ) {
      toast.error("Your subscription has expired.");
      navigate("/subscriptions");
      return;
    }

    if (!subscription.selectedPlan || !subscription.selectedPackageDetails) {
      toast.error("Subscription details are incomplete.");
      navigate("/subscriptions");
      return;
    }

    const packageType = subscription.packageType;
    const allowedBookLimit =
      {
        ONE_BOOK: 1,
        TWO_BOOKS: 2,
        THREE_BOOKS: 3,
      }[subscription?.packageType] || 0;

    setAllowedBookLimit(allowedBookLimit);

    // Calculate active orders count properly
    const activeOrdersCount = (userOrders || []).filter(
      (o) => o?.returnStatus === "NOT_RETURNED"
    ).length;

    const remaining = allowedBookLimit - activeOrdersCount;
    setRemainingBooks(remaining);

    // Use the calculated count in the check
    if (activeOrdersCount >= allowedBookLimit) {
      toast.error(
        `You have reached your borrowing limit of ${allowedBookLimit} books.`
      );
      navigate("/myOrders");
      return;
    }

    if (!transformedAddress) {
      toast.error("No address found. Please add address to continue.");
      return;
    }

    try {
      setLoading(true);
      const result = await dispatch(
        createNewOrder({ userId, bookId })
      ).unwrap();
      setOrder(result);
      toast.success("Order placed successfully!");
      setOrderPlaced(true);
    } catch (err) {
      toast.error(err.message || "Failed to place order.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4">Loading details...</div>;

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) return <SkeletonLoader />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-shade1 to-white p-6 font-nunito">
      <div className="max-w-6xl mx-auto space-y-8">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-text-black mb-8"
        >
          Confirm Your Order
        </motion.h2>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Book Details Column */}
          <motion.div
            variants={cardVariants}
            className="lg:col-span-2 space-y-6"
          >
            {/* Book Card */}
            <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="flex flex-col md:flex-row gap-6">
                {bookDetails.images?.[0]?.url && (
                  <div className="relative flex-shrink-0">
                    <img
                      src={`${config.url}${bookDetails.images[0].url}`}
                      alt={bookDetails.title}
                      className="w-40 h-64 object-fit rounded-xl shadow-lg hover:scale-105 transition-transform"
                    />
                    <div className="absolute top-2 right-2 bg-primary/90 text-white px-3 py-1 rounded-full text-sm">
                      {bookDetails.category?.name}
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-text-black">
                    {bookDetails.title}
                  </h3>
                  <p className="text-lg text-text-muted flex items-center gap-2">
                    <BookOpen size={18} className="text-primary" />
                    {bookDetails.author?.name || "Unknown Author"}
                  </p>
                  <p className="text-text-muted leading-relaxed">
                    {bookDetails.description || "No description available."}
                  </p>
                </div>
              </div>
            </div>
            {/* —————— Address Card —————— */}
            <motion.div
              variants={cardVariants}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              {/* … header … */}
              {rawAddress ? (
                <div className="space-y-2">
                  <p className="text-lg">{rawAddress.fullAddress}</p>
                  <p>
                    {rawAddress.city.cityName}, {rawAddress.city.state}{" "}
                    {rawAddress.pincode.pinCode}
                  </p>
                  <button
                    onClick={() => setModalOpen(true)} // open for “edit”
                    className="text-primary underline"
                  >
                    Edit Address
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setModalOpen(true)} // open for “add”
                  className="text-red-500 underline"
                >
                  Add Delivery Address
                </button>
              )}
            </motion.div>

            {/* —————— The Modal —————— */}
            <AddAddressModal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              userId={userId}
              cityOptions={cityOptions}
              pincodeOptions={pincodeOptions}
              onSuccess={() => dispatch(fetchUserAddress(userId))}
              initialData={
                rawAddress
                  ? {
                      fullAddress: rawAddress.fullAddress,
                      cityId: rawAddress.city.cityId,
                      pincodeId: rawAddress.pincode.pincodeId,
                    }
                  : null
              }
            />
          </motion.div>

          {/* Order Summary Column */}
          <div className="lg:col-span-1">
            <motion.div
              variants={cardVariants}
              className="bg-white rounded-2xl p-6 shadow-xl sticky top-6"
            >
              <AnimatePresence mode="wait">
                {orderPlaced ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center space-y-6"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto animate-pop-in" />
                    <h3 className="text-2xl font-bold text-text-black">
                      Order Confirmed!
                    </h3>
                    <div className="space-y-4 text-left">
                      <div className="flex justify-between">
                        <span className="text-text-muted">Order ID:</span>
                        <span className="font-medium">
                          #{order.orderId}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted">Status:</span>
                        <span className="text-primary font-semibold">
                          {order.orderStatus}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted">Delivery:</span>
                        <span className="font-medium">
                          {order.deliveryStatus}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/myOrders")}
                      className="w-full bg-primary text-white py-3 rounded-xl hover:bg-primary-shade5 transition-colors font-semibold"
                    >
                      View My Orders
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-6"
                  >
                    <h3 className="text-xl font-bold text-text-black mb-4">
                      Order Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-text-muted">
                          Subscription Plan:
                        </span>
                        <span className="font-semibold">
                          {subscription?.planName}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-muted">Package Type:</span>
                        <span className="font-semibold">
                          {subscription?.packageType?.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-muted">
                          Books Remaining:
                        </span>
                        <span className="font-semibold text-primary">
                          {remainingBooks} / {allowedBookLimit}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-muted">Delivery:</span>
                        <span className="font-semibold">Free</span>
                      </div>
                    </div>
                    <button
                      onClick={handleOrder}
                      disabled={loading}
                      className="w-full bg-primary text-white py-3 rounded-xl hover:bg-primary-shade5 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Clock className="animate-pulse" size={18} />
                          Processing...
                        </>
                      ) : (
                        "Confirm Order"
                      )}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
